import { useState } from "react";
import { Building2, Users, LogOut, Search, TrendingUp, Menu, X, Euro } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Avatar, AvatarFallback } from "../ui/avatar";
import UserNameDisplay from "../userNameDisplay";
import { useDatabase } from "../DatabaseProvider";

interface AdminLayoutProps {
  children: React.ReactNode;
  currentPage: string;
  onPageChange: (page: string) => void;
  onLogout: () => void;
}

const menuItems = [
  { id: "rendimiento", label: "Rendimiento", icon: TrendingUp },
  { id: "activos", label: "Activos", icon: Building2 },
  { id: "inversores", label: "Inversores", icon: Users },
  { id: "fees", label: "Fees", icon: Euro },
];

export function AdminLayout({ children, currentPage, onPageChange, onLogout }: AdminLayoutProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useDatabase();

  const getInitials = () => {
    if (user.loading) return "";
    if (!user.isAuthenticated || !user.user) return "U";
    const first = (user.user.first_name || "").trim();
    const last = (user.user.last_name || "").trim();
    const fallback = (user.user.email || "").split("@")[0] || "U";
    const name = (first || last) ? `${first} ${last}`.trim() : fallback;
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "U";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:flex-col w-64 bg-white border-r border-gray-200 fixed h-screen">
        {/* Logo */}
        <div className="p-6 border-b border-gray-100">
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => onPageChange("rendimiento")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') onPageChange('rendimiento'); }}
          >
            <div className="w-10 h-10 bg-[#2B59FF] rounded-lg flex items-center justify-center">
              <span className="text-white" style={{ fontWeight: 700 }}>B&P</span>
            </div>
            <div className="flex flex-col">
              <span className="text-sm text-gray-900" style={{ fontWeight: 600 }}>Bellamy & Partners</span>
              <span className="text-xs text-gray-500">Real Assets Mgmt</span>
            </div>
          </div>
        </div>

        {/* Menu */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentPage === item.id;
            
            return (
              <button
                key={item.id}
                onClick={() => onPageChange(item.id)}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-150
                  ${isActive 
                    ? "bg-gray-100 text-gray-900 shadow-sm" 
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }
                `}
              >
                <Icon size={20} />
                <span className="text-sm" style={{ fontWeight: isActive ? 600 : 400 }}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </nav>

        {/* User Profile */}
        <div className="mt-auto p-4 border-t border-gray-100">
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-gray-50">
            <Avatar className="w-9 h-9">
              <AvatarFallback className="bg-indigo-100 text-indigo-600 text-sm">
                {getInitials()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <UserNameDisplay showRole />
            </div>
          </div>
          
          <Button
            variant="ghost"
            onClick={onLogout}
            className="w-full mt-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 justify-start"
          >
            <LogOut size={18} className="mr-2" />
            Cerrar sesión
          </Button>
        </div>
      </aside>

      {/* Mobile Sidebar (drawer) */}
      <div className={`fixed inset-0 z-50 md:hidden ${sidebarOpen ? "" : "pointer-events-none"}`}>
        <div
          className={`absolute inset-0 bg-black/40 transition-opacity ${sidebarOpen ? "opacity-100" : "opacity-0"}`}
          onClick={() => setSidebarOpen(false)}
        />
        <div className={`relative w-64 h-full bg-white border-r border-gray-200 transform transition-transform flex flex-col ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
          {/* Logo + close */}
          <div className="p-6 border-b border-gray-100 flex items-center justify-between">
            <div
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => { setSidebarOpen(false); onPageChange("rendimiento"); }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') { setSidebarOpen(false); onPageChange('rendimiento'); } }}
            >
              <div className="w-10 h-10 bg-[#2B59FF] rounded-lg flex items-center justify-center">
                <span className="text-white" style={{ fontWeight: 700 }}>B&P</span>
              </div>
              <div className="flex flex-col">
                <span className="text-sm text-gray-900" style={{ fontWeight: 600 }}>Bellamy & Partners</span>
                <span className="text-xs text-gray-500">Real Assets Mgmt</span>
              </div>
            </div>
            <button className="p-2 rounded-md" onClick={() => setSidebarOpen(false)} aria-label="Cerrar menu">
              <X size={18} />
            </button>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-auto">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentPage === item.id;

              return (
                <button
                  key={item.id}
                  onClick={() => {
                    onPageChange(item.id);
                    setSidebarOpen(false);
                  }}
                  className={
                    `
                    w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-150
                    ${isActive 
                      ? "bg-gray-100 text-gray-900 shadow-sm" 
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                    }
                  `
                  }
                >
                  <Icon size={20} />
                  <span className="text-sm" style={{ fontWeight: isActive ? 600 : 400 }}>
                    {item.label}
                  </span>
                </button>
              );
            })}
          </nav>

          <div className="mt-auto p-4 border-t border-gray-100">
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-gray-50">
              <Avatar className="w-9 h-9">
                <AvatarFallback className="bg-indigo-100 text-indigo-600 text-sm">
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <UserNameDisplay showRole />
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={() => { setSidebarOpen(false); onLogout(); }}
              className="w-full mt-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 justify-start"
            >
              <LogOut size={18} className="mr-2" />
              Cerrar sesión
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <style>{`
        /* Ensure main content does not overflow viewport when sidebar is fixed (16rem) */
        .admin-main { box-sizing: border-box; }
        @media (min-width: 768px) {
          .admin-main { max-width: calc(100vw - 16rem); overflow-x: hidden; }
        }
      `}</style>
      <div className="flex-1 md:ml-64 ml-0 admin-main">
        {/* Navbar */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
          <div className="px-8 py-4 flex items-center justify-between gap-4">
            {/* Mobile menu button */}
            <div className="md:hidden">
              <Button
                variant="ghost"
                size="icon"
                className="relative h-10 w-10 text-gray-600 hover:text-gray-900"
                onClick={() => setSidebarOpen(true)}
                aria-label="Abrir menú"
              >
                <Menu size={18} />
              </Button>
            </div>

            {/* Search Bar (hidden until feature is ready) */}
            <div className="hidden">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                type="text"
                placeholder="Buscar activo o inversor..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-10 bg-gray-50 border-gray-200 focus:border-[#2B59FF] focus:ring-[#2B59FF]"
              />
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-3">

              <Button
                variant="ghost"
                size="icon"
                className="relative h-10 w-10 text-gray-600 hover:text-gray-900"
              >
                {/* Botón de notificaciones ocultado — se deja comentado hasta que esté funcional */}
                {/*
                <Bell size={20} />
                */}
              </Button>

              <Avatar className="w-10 h-10">
                <AvatarFallback className="bg-[#2B59FF] text-white">
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-8">
          {children}
        </main>
      </div>
    </div>
  );
}